<!-- frontend/src/views/Analysis.vue -->
<template>
  <div class="container my-5">
    <!-- Zurück-Button -->
    <button @click="goBack" class="btn btn-secondary mb-4">
      <font-awesome-icon icon="arrow-left" /> Zurück zum Hauptmenü
    </button>

    <h2 class="text-center mb-4">Analyse</h2>

    <!-- Filter -->
    <div class="row g-3 mb-4">
      <div class="col-12 col-md-3">
        <label class="form-label">Von:</label>
        <input type="date" v-model="filters.from" class="form-control" />
      </div>
      <div class="col-12 col-md-3">
        <label class="form-label">Bis:</label>
        <input type="date" v-model="filters.to" class="form-control" />
      </div>
      <div class="col-12 col-md-3">
        <label class="form-label">Budget pro Woche:</label>
        <input type="number" v-model.number="budget" class="form-control" />
      </div>
      <div class="col-12 col-md-3 d-flex align-items-end">
        <button class="btn btn-primary w-100" @click="fetchAnalytics">Anwenden</button>
      </div>
    </div>

    <!-- Kennzahlen -->
    <div class="row g-4 mb-5">
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body text-center">
            <h5 class="card-title mb-3">Gesamt-Umsatz</h5>
            <div class="display-6">{{ $formatPrice(profitData.revenue) }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body text-center">
            <h5 class="card-title mb-3">Gesamt-Gewinn</h5>
            <div class="display-6">{{ $formatPrice(profitData.profit) }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body text-center">
            <h5 class="card-title mb-3">Auszahlungen offen</h5>
            <div class="display-6">{{ $formatPrice(outstandingPayouts) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts im Grid -->
    <div class="row g-4 mb-5">
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Umsatz vs. Budget (Wöchentlich)</h5>
            <div class="ratio ratio-16x9">
              <canvas id="salesTrendChart"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Gewinnentwicklung (Wöchentlich)</h5>
            <div class="ratio ratio-16x9">
              <canvas id="profitTrendChart"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Umsatz pro Wochentag</h5>
            <div class="ratio ratio-16x9">
              <canvas id="weekdaySalesChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4 mb-5">
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Monats-Umsatz</h5>
            <div class="ratio ratio-16x9">
              <canvas id="monthlyRevenueChart"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Auszahlungen pro Monat</h5>
            <div class="ratio ratio-16x9">
              <canvas id="payoutChart"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="card-title">Artikel-Status Übersicht</h5>
            <div class="ratio ratio-16x9">
              <canvas id="statusChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Umsatz Jahr für Jahr (seit Beginn) -->
    <div class="row g-4 mb-5">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Umsatzentwicklung: Jahr für Jahr</h5>
            <p class="text-muted small mb-2">Monatlicher Umsatz pro Jahr – alle Daten seit Beginn</p>
            <div class="ratio ratio-21x9">
              <canvas id="salesByYearChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/services/axios';

import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  data() {
    const pad = n => String(n).padStart(2, '0');
    const toLocalDateStr = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      filters: {
        from: toLocalDateStr(firstOfMonth),
        to: toLocalDateStr(lastOfMonth)
      },
      budget: 600,

      salesTrend: [],
      profitData: {},
      outstandingPayouts: 0,
      profitTrend: [],
      weekdaySales: [],
      monthlyRevenue: [],
      payoutsPerMonth: [],
      salesByYear: [],
      articleStatus: [],

      charts: {
        salesTrendChart: null,
        profitTrendChart: null,
        weekdaySalesChart: null,
        monthlyRevenueChart: null,
        payoutChart: null,
        statusChart: null,
        salesByYearChart: null
      }
    };
  },
  methods: {
    async fetchAnalytics() {
      const { from, to } = this.filters;
      const paramsCommon = { from, to };
      const [
        trendRes, profitRes, outstandingRes, profitTrendRes,
        weekdayRes, monthRes,
        payoutsRes, salesByYearRes, statusRes
      ] = await Promise.all([
        axios.get('/analytics/sales-trend',      { params: { ...paramsCommon, budget: this.budget } }),
        axios.get('/analytics/profit',           { params: paramsCommon }),
        axios.get('/analytics/outstanding-payouts'),
        axios.get('/analytics/profit-trend',     { params: paramsCommon }),
        axios.get('/analytics/sales-by-weekday', { params: paramsCommon }),
        axios.get('/analytics/sales-per-month',  { params: paramsCommon }),
        axios.get('/analytics/payouts-per-month',{ params: paramsCommon }),
        axios.get('/analytics/sales-by-year'),
        axios.get('/analytics/article-status')
      ]);

      this.salesTrend       = trendRes.data;
      this.profitData       = profitRes.data;
      this.outstandingPayouts = outstandingRes.data.outstandingPayouts ?? 0;
      this.profitTrend     = profitTrendRes.data;
      this.weekdaySales    = weekdayRes.data;
      this.monthlyRevenue  = monthRes.data;
      this.payoutsPerMonth = payoutsRes.data;
      this.salesByYear     = salesByYearRes.data;
      this.articleStatus   = statusRes.data;

      this.renderSalesTrendChart();
      this.renderProfitTrendChart();
      this.renderWeekdaySalesChart();
      this.renderMonthlyRevenueChart();
      this.renderPayoutChart();
      this.renderStatusChart();
      this.renderSalesByYearChart();
    },

    renderSalesTrendChart() {
      if (this.charts.salesTrendChart) this.charts.salesTrendChart.destroy();
      const labels  = this.salesTrend.map(i => `KW${i.week} ${i.year}`);
      const data    = this.salesTrend.map(i => this.$roundToFranc(i.totalSales));
      const budgets = this.salesTrend.map(i => this.$roundToFranc(i.budget));
      const ctx     = document.getElementById('salesTrendChart').getContext('2d');
      this.charts.salesTrendChart = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets: [
          { label: 'Umsatz', data },
          { label: 'Budget', data: budgets, type: 'line' }
        ]},
        options: { plugins: { legend: { position: 'top' } } }
      });
    },

    renderProfitTrendChart() {
      if (this.charts.profitTrendChart) this.charts.profitTrendChart.destroy();
      const labels = this.profitTrend.map(i => `KW${i.week} ${i.year}`);
      const data   = this.profitTrend.map(i => this.$roundToFranc(i.profit || 0));
      const ctx    = document.getElementById('profitTrendChart').getContext('2d');
      this.charts.profitTrendChart = new Chart(ctx, {
        type: 'line',
        data: { labels, datasets: [ { label: 'Gewinn', data } ] },
        options: { plugins: { legend: { position: 'top' } } }
      });
    },

    renderWeekdaySalesChart() {
      if (this.charts.weekdaySalesChart) this.charts.weekdaySalesChart.destroy();
      const labels = ['So','Mo','Di','Mi','Do','Fr','Sa'];
      const counts = Array(7).fill(0);
      this.weekdaySales.forEach(i => { counts[i.weekday-1] = this.$roundToFranc(i.totalSales); });
      const ctx = document.getElementById('weekdaySalesChart').getContext('2d');
      this.charts.weekdaySalesChart = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets: [ { label: 'Umsatz', data: counts } ] },
        options: { plugins: { legend: { position: 'top' } } }
      });
    },

    renderMonthlyRevenueChart() {
      if (this.charts.monthlyRevenueChart) this.charts.monthlyRevenueChart.destroy();
      const labels = this.monthlyRevenue.map(i => `${i.month}/${i.year}`);
      const data   = this.monthlyRevenue.map(i => this.$roundToFranc(i.totalSales));
      const ctx    = document.getElementById('monthlyRevenueChart').getContext('2d');
      this.charts.monthlyRevenueChart = new Chart(ctx, {
        type: 'line',
        data: { labels, datasets: [ { label: 'Umsatz', data } ] },
        options: { plugins: { legend: { position: 'top' } } }
      });
    },

    renderPayoutChart() {
      if (this.charts.payoutChart) this.charts.payoutChart.destroy();
      const labels = this.payoutsPerMonth.map(p => `${p.month}/${p.year}`);
      const data   = this.payoutsPerMonth.map(p => this.$roundToFranc(p.totalPayout));
      const ctx    = document.getElementById('payoutChart').getContext('2d');
      this.charts.payoutChart = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets: [ { label: 'Auszahlungen', data } ] },
        options: { plugins: { legend: { position: 'top' } } }
      });
    },

    renderStatusChart() {
      if (this.charts.statusChart) this.charts.statusChart.destroy();
      const labels = this.articleStatus.map(s => s.ArticleStatus);
      const data   = this.articleStatus.map(s => s.count);
      const ctx    = document.getElementById('statusChart').getContext('2d');
      this.charts.statusChart = new Chart(ctx, {
        type: 'pie',
        data: { labels, datasets: [ { label: 'Artikel-Status', data } ] },
        options: { plugins: { legend: { position: 'top' } } }
      });
    },

    renderSalesByYearChart() {
      if (this.charts.salesByYearChart) this.charts.salesByYearChart.destroy();
      const monthLabels = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
      const byYear = {};
      let firstYear = Infinity, firstMonth = 13;
      this.salesByYear.forEach(row => {
        const y = row.year, m = row.month;
        if (!byYear[y]) byYear[y] = Array(12).fill(null);
        byYear[y][m - 1] = this.$roundToFranc(row.totalSales || 0);
        if (y < firstYear || (y === firstYear && m < firstMonth)) {
          firstYear = y;
          firstMonth = m;
        }
      });
      const years = Object.keys(byYear).map(Number).sort((a, b) => a - b);
      const el = document.getElementById('salesByYearChart');
      if (years.length === 0 || !el) return;
      const now = new Date();
      const currYear = now.getFullYear(), currMonth = now.getMonth() + 1;
      const datasets = years.map((yr, i) => {
        const raw = byYear[yr];
        const data = raw.map((val, idx) => {
          const m = idx + 1;
          if (yr === firstYear && m < firstMonth) return null;
          if (yr === currYear && m > currMonth) return null;
          return val === null ? 0 : val;
        });
        return {
          label: String(yr),
          data,
          spanGaps: false,
          borderColor: ['#0d6efd','#198754','#dc3545','#fd7e14','#6f42c1','#20c997','#e83e8c'][i % 7],
          backgroundColor: ['#0d6efd','#198754','#dc3545','#fd7e14','#6f42c1','#20c997','#e83e8c'][i % 7] + '20',
          fill: false,
          tension: 0.3
        };
      });
      const ctx = el.getContext('2d');
      this.charts.salesByYearChart = new Chart(ctx, {
        type: 'line',
        data: { labels: monthLabels, datasets },
        options: {
          plugins: { legend: { position: 'top' } },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: 'Umsatz (CHF)' } },
            x: { title: { display: true, text: 'Monat' } }
          }
        }
      });
    },

    goBack() {
      this.$router.push('/mainmenu');
    }
  },

  mounted() {
    this.fetchAnalytics();
  }
};
</script>

<style scoped>
/* Nutzt Bootstrap-Cards, Grid und Ratio für responsives Design */
</style>
